import Component from '@glimmer/component'
import { action } from '@ember/object'
import type { RolloverStep } from 'teacher-ui-client/school-rollover/steps'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'

interface Args {
  stepItems: { stepId: RolloverStep; stepLabel: string }[]
  currentStepId: RolloverStep
  highestUnlockedStepId?: RolloverStep
  goToStep: (stepId: RolloverStep) => void
  subscriptionType: SubscriptionType
}

interface Signature {
  Args: Args
  Element: HTMLDivElement
}

interface StateClassNames {
  active: string
  unlocked: string
  locked: string
}

const themes: Partial<Record<SubscriptionType, StateClassNames>> = {
  reading: {
    active: 'bg-oceany-blue-350 text-white after:border-l-oceany-blue-350',
    unlocked:
      'bg-oceany-blue-300 text-white cursor-pointer after:border-l-oceany-blue-300 hover:bg-oceany-blue-350 hover:after:border-l-oceany-blue-350 focus:bg-oceany-blue-350 focus:after:border-l-oceany-blue-350',
    locked: 'bg-dusty-black-100 text-dusty-black-200 ',
  },
  maths: {
    active: 'bg-ms-green-350 text-white after:border-l-ms-green-350',
    unlocked:
      'bg-ms-green-300 text-white cursor-pointer after:border-l-ms-green-300 hover:bg-ms-green-350 hover:after:border-l-ms-green-350 focus:bg-ms-green-350 focus:after:border-l-ms-green-350',
    locked: 'bg-dusty-black-100 text-dusty-black-200 ',
  },
}

export class StepsNavComponent extends Component<Signature> {
  get themeClasses(): StateClassNames {
    const defaultTheme = { active: '', unlocked: '', locked: '' }
    return themes[this.args.subscriptionType] ?? defaultTheme
  }

  get stepItemsWithMeta() {
    const { stepItems, currentStepId, highestUnlockedStepId } = this.args

    const stepItemIds = stepItems.map((stepItem) => stepItem.stepId)

    let unlockedStepId = ''
    if (highestUnlockedStepId && stepItemIds.includes(highestUnlockedStepId)) {
      unlockedStepId = highestUnlockedStepId
    } else {
      unlockedStepId = currentStepId
    }

    return stepItems.map(({ stepId, stepLabel }, stepIndex) => {
      const isFirst = stepIndex === 0
      const isCurrent = stepId === currentStepId
      const isLast = stepIndex === stepItems.length - 1

      const isUnlocked = stepIndex <= stepItemIds.indexOf(unlockedStepId as RolloverStep)

      return { stepId, stepLabel, isFirst, isCurrent, isLast, isUnlocked }
    })
  }

  @action
  goToStep({ isUnlocked, isCurrent, stepId }: { isUnlocked: boolean; isCurrent: boolean; stepId: RolloverStep }): void {
    if (isUnlocked && !isCurrent) {
      this.args.goToStep(stepId)
    }
  }

  <template>
    <div class="mx-auto flex w-full flex-wrap justify-center" data-test-steps-nav ...attributes>
      {{#each this.stepItemsWithMeta as |stepItem|}}
        <step-item
          data-test-step-state={{if stepItem.isCurrent "active-step" (if stepItem.isUnlocked "unlocked-step")}}
          class="relative mx-2 my-1 h-[30px] leading-[30px] font-bold
            {{unless
              stepItem.isFirst
              'before:absolute before:z-0 before:h-0 before:w-0 before:border-y-15 before:border-r-0 before:border-l-15 before:border-solid before:border-y-transparent before:border-r-transparent before:border-l-white before:content-[""]'
            }}
            {{unless
              stepItem.isLast
              'after:border-right-0 after:border-l-dusty-black-100 after:absolute after:right-[-15px] after:z-1 after:h-0 after:w-0 after:border-y-15 after:border-l-15 after:border-solid after:border-y-transparent after:border-r-transparent after:content-[""]'
            }}
            {{if
              stepItem.isCurrent
              this.themeClasses.active
              (if stepItem.isUnlocked this.themeClasses.unlocked this.themeClasses.locked)
            }}
            "
          role="link"
          {{on "click" (fn this.goToStep stepItem)}}
          data-test-step={{stepItem.stepId}}
        >
          <span class="pr-3 pl-6">{{stepItem.stepLabel}}</span>
        </step-item>
      {{/each}}
    </div>
  </template>
}

export default StepsNavComponent
